import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';

import { PVSProvider, getCustomersFromDb } from '@/utils/helpers.js';

import { useCustomersStore, useLocationStore, usePrismicStore } from '@/zustandStore.js';

import useAuth from './useAuth.ts';

const adminsBaseUrl = `https://${import.meta.env.VITE_ADMIN_API_DOMAIN}`;
const attributesUrl = `${adminsBaseUrl}/secure-admin/users/attributes`;

const useLocations = () => {
    const [availableLocations, setAvailableLocations] = useState(undefined);
    const { user, userToken: authToken } = useAuth();
    const instanceConfigs = usePrismicStore((state) => state.instanceConfigs);
    const locationHubs = usePrismicStore((state) => state.locationHubs);
    const currentCustomer = usePrismicStore((state) => state.currentCustomer);
    const userInstanceConfigs = useLocationStore((state) => state.userInstanceConfigs);

    const isOutdatedInstanceConfigStructure = useMemo(() => {
        if (!instanceConfigs) return false;

        const values = Object.values(instanceConfigs);
        return !Array.isArray(values[0]);
    }, [instanceConfigs]);

    const getUserAttributes = async (customerId) => {
        try {
            const response = await axios.get(attributesUrl, {
                headers: {
                    customer_id: customerId,
                    Authorization: authToken,
                },
            });

            return response.data?.data;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    };

    const getLocationKeyMap = async (customerId) => {
        const customers = await getCustomersFromDb(customerId, authToken);
        if (!customers) return undefined;

        const locationKeyMap = new Map();
        for (const customer of customers) {
            for (const location of customer.locations) {
                if (!location.prismic_key || !location.id) continue;

                locationKeyMap.set(location.id, location.prismic_key);
            }
        }

        return locationKeyMap;
    };

    const getAvailableLocations = async () => {
        if (user.groups?.includes('admin')) {
            setAvailableLocations([]);
            return;
        }

        const [locationKeyMap, userAttributes] = await Promise.all([
            getLocationKeyMap(user.customerIds[0]),
            getUserAttributes(user.customerIds[0]),
        ]);

        if (!locationKeyMap || !userAttributes) return;

        const new_available_locations = [];

        for (const location_id of userAttributes.location_ids) {
            const location_key = locationKeyMap.get(location_id);
            if (!location_key) continue;

            new_available_locations.push(location_key);
        }

        useCustomersStore.setState({ locationKeyMap });
        setAvailableLocations(new_available_locations);
    };

    useEffect(() => {
        if (!user || !authToken) return;

        getAvailableLocations();
    }, [user, authToken]);

    useEffect(() => {
        if (!user || !locationHubs || !currentCustomer || !userInstanceConfigs || !availableLocations) return;

        const availableLocationsSet = new Set(availableLocations);

        if (!user.groups) {
            useLocationStore.setState({ hasFailed: true });
            return;
        }

        const customerLabel =
            currentCustomer.key?.[0]?.text ??
            currentCustomer.customer_name?.[0]?.text ??
            currentCustomer.id ??
            'unknown';

        const currentCustomerLocationIds = currentCustomer.locations.map(({ location }) => location.id);

        // Surface (instead of crash on) hubs this customer references that are loaded for the current
        // environment but have no instance config — i.e. a misconfigured Prismic config_dc_instance
        // (missing/empty instance_id, or wrong environment tag). Naming the customer + hub turns a white
        // screen into an actionable console error pointing at the exact broken document.
        const misconfiguredHubs = locationHubs.filter(
            (hub) => currentCustomerLocationIds.includes(hub.id) && !Array.isArray(userInstanceConfigs[hub.id])
        );
        if (misconfiguredHubs.length > 0) {
            console.error(
                `[useLocations] BROKEN customer "${customerLabel}": ${misconfiguredHubs.length} location hub(s) have no ` +
                    `instance config for this environment and were skipped. Fix the Prismic config_dc_instance ` +
                    `(needs the env tag + a non-empty instance_id) for: ${misconfiguredHubs
                        .map((hub) => `"${hub.data.title?.[0]?.text ?? '?'}" (hub id ${hub.id})`)
                        .join(', ')}`
            );
        }

        const locations = locationHubs
            .filter(
                (hub) =>
                    (user.groups?.includes('admin') || availableLocationsSet.has(hub.data.key[0].text)) &&
                    currentCustomerLocationIds.includes(hub.id) &&
                    // Skip hubs with no usable instance config — missing (logged above) or empty after
                    // per-user filtering. A 0-config hub yields an undefined `dcConfig`, which many call
                    // sites (e.g. selectedLocation.dcConfig.is_filtering_active) dereference and would crash.
                    userInstanceConfigs[hub.id]?.length > 0
            )
            .map((hub) => {
                const hubConfigs = userInstanceConfigs[hub.id];
                return {
                    name: hub.data.title[0].text,
                    key: hub.data.key[0].text,
                    instanceIds: hubConfigs.map((config) => config.instance_id[0].text),
                    dcConfig: hubConfigs.find((config) => config.provider === PVSProvider.DOC_CIRRUS),
                    configs: hubConfigs,
                };
            });

        if (locations.length === 0) {
            console.error(
                `[useLocations] BROKEN customer "${customerLabel}": resolved to 0 usable locations. Check its hub ` +
                    `publishing/environment tags and config_dc_instance provisioning in Prismic.`
            );
        }

        useLocationStore.setState({ locations: locations, hasFailed: false });

        const selectedLocation =
            locations.find((location) => location.key === localStorage.getItem('selectedLocation')) ?? locations[0];

        // Fall back to an empty-but-shaped location so render-time readers of `.configs`/`.instanceIds`
        // (e.g. selectedLocation.configs.map(...)) don't crash when a customer has no resolvable locations.
        useLocationStore.setState({ selectedLocation: selectedLocation ?? { configs: [], instanceIds: [] } });
    }, [user, locationHubs, currentCustomer, userInstanceConfigs, availableLocations]);

    useEffect(() => {
        if (!user || !instanceConfigs || isOutdatedInstanceConfigStructure) return;

        if (user.groups?.includes('admin')) {
            useLocationStore.setState({ userInstanceConfigs: instanceConfigs });
            return;
        }

        const instanceIdSet = new Set(user.instanceIds ?? []);

        const availableInstanceConfigs = {};

        for (const key of Object.keys(instanceConfigs)) {
            let configs = [...instanceConfigs[key]];
            configs = configs.filter((config) => instanceIdSet.has(config.instance_id[0].text));
            availableInstanceConfigs[key] = configs;
        }

        useLocationStore.setState({ userInstanceConfigs: availableInstanceConfigs });
    }, [instanceConfigs, user, isOutdatedInstanceConfigStructure]);
};

export default useLocations;
